import React, { useCallback, useEffect } from 'react'
import { TextInput, FlexRow, FlexColumn, Select } from 'components/lib'
import { MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md'
import SuperSwitch from 'components/lib/Forms/Switch'
import { FormikErrors, FormikTouched } from 'formik'
import {
  DaySchedule,
  TimeSlot,
  ScheduleData,
} from '../../../utils/schedule/types'
import { PERIOD_OPTIONS } from '../../../utils/schedule/constants'
import {
  isValidTimeFormat,
  isValidTimeRange,
} from '../../../utils/schedule/timeUtils'
import { ScheduleValidator } from '../../../utils/schedule/validation'

interface ScheduleFieldProps {
  value: ScheduleData
  onChange: (newSchedule: ScheduleData) => void
  errors?: FormikErrors<any>
  touched?: FormikTouched<any>
  onValidationChange?: (isValid: boolean) => void
}

// Constants
const ROW_HEIGHT = 40

// Format time as user types
const formatTimeInput = (value: string): string => {
  const digits = value.replace(/[^\d]/g, '')

  if (digits.length === 0) return ''
  if (digits.length <= 2) return digits

  // Insert colon after first 1-2 digits
  const hours = digits.slice(0, digits.length <= 3 ? 1 : 2)
  const minutes = digits.slice(hours.length)

  return `${hours}:${minutes}`
}

// Convert 24-hour time when input is complete
const tryConvert24Hour = (
  value: string,
  on24HourDetected?: (period: 'AM' | 'PM', convertedTime: string) => void,
): string => {
  const match = value.match(/^(\d{1,2}):(\d{2})$/)
  if (!match) return value

  const hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)

  // Handle 24-hour time (13:00-23:59)
  if (hours >= 13 && hours <= 23 && minutes <= 59) {
    const hours12 = hours - 12
    const convertedTime = `${hours12}:${minutes.toString().padStart(2, '0')}`

    // Trigger period change immediately
    if (on24HourDetected) {
      on24HourDetected('PM', convertedTime)
    }

    return convertedTime
  }

  return value
}

// Time Input Component
const TimeInput: React.FC<{
  value: string
  onChange: (value: string) => void
  on24HourDetected?: (period: 'AM' | 'PM', convertedTime: string) => void
  placeholder: string
  isValid: boolean
  hasRangeError?: boolean
}> = ({
  value,
  onChange,
  on24HourDetected,
  placeholder,
  isValid,
  hasRangeError,
}) => {
  // Show error styling if value is invalid or has range error
  const showError = (!isValid && value.length > 0) || hasRangeError

  return (
    <TextInput
      value={value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        // Just format with colon as they type, no conversion
        const formatted = formatTimeInput(e.target.value)
        onChange(formatted)
      }}
      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
        let handled = false
        const converted = tryConvert24Hour(
          e.target.value,
          (period: 'AM' | 'PM', convertedTime: string) => {
            handled = true
            if (on24HourDetected) {
              on24HourDetected(period, convertedTime)
            }
          },
        )
        if (!handled) {
          onChange(converted)
        }
      }}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        // Prevent form submission or navigation on Enter
        if (e.key === 'Enter') {
          e.preventDefault()
          e.currentTarget.blur() // Just blur the input to trigger conversion
        }
      }}
      placeholder={placeholder}
      inputMode="numeric"
      pattern="[0-9:]*"
      style={{
        borderRadius: '4px 0 0 4px',
        height: ROW_HEIGHT,
        width: 56,
        padding: '0 8px',
        textAlign: 'center',
        fontSize: 14,
        borderRight: 'none',
        borderColor: showError ? '#EF4444' : '#E0E0E0',
        backgroundColor: showError ? '#FEF2F2' : 'white',
      }}
      maxLength={5}
    />
  )
}

// Period Select Component
const PeriodSelect: React.FC<{
  value: string
  onChange: (value: string) => void
  hasRangeError?: boolean
}> = ({ value, onChange, hasRangeError }) => (
  <Select
    value={value}
    onChange={e => onChange(e.target.value)}
    style={{
      width: 38,
      borderRadius: '0 4px 4px 0',
      height: ROW_HEIGHT,
      fontSize: 14,
      textAlign: 'center',
      padding: 0,
      borderColor: hasRangeError ? '#EF4444' : '#E0E0E0',
      backgroundColor: hasRangeError ? '#FEF2F2' : 'white',
      appearance: 'none',
      backgroundImage: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
    }}
  >
    {PERIOD_OPTIONS.map(period => (
      <option key={period} value={period}>
        {period}
      </option>
    ))}
  </Select>
)

// --- Utility functions for slot creation ---
const createDefaultTimeSlot = (): TimeSlot => ({
  id: `slot-${Date.now()}`,
  startTime: '9:00',
  startPeriod: 'AM',
  endTime: '5:00',
  endPeriod: 'PM',
  allDay: false,
})

const createAllDaySlot = (): TimeSlot => ({
  id: `slot-${Date.now()}`,
  allDay: true,
  startTime: '',
  startPeriod: 'AM',
  endTime: '',
  endPeriod: 'PM',
})

// Time Slot Input Component
const TimeSlotInput = React.memo<{
  slot: TimeSlot
  dayIndex: number
  slotIndex: number
  onChange: (
    dayIdx: number,
    slotIdx: number,
    field: keyof TimeSlot,
    value: string,
  ) => void
  onChangeWithPeriod: (
    dayIdx: number,
    slotIdx: number,
    timeField: 'startTime' | 'endTime',
    value: string,
    period: 'AM' | 'PM',
  ) => void
}>(({ slot, dayIndex, slotIndex, onChange, onChangeWithPeriod }) => {
  const isStartValid = isValidTimeFormat(slot.startTime)
  const isEndValid = isValidTimeFormat(slot.endTime)
  const isRangeValid =
    isStartValid &&
    isEndValid &&
    isValidTimeRange(
      slot.startTime,
      slot.startPeriod,
      slot.endTime,
      slot.endPeriod,
    )
  const hasRangeError = isStartValid && isEndValid && !isRangeValid

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 0,
        marginBottom: -8,
      }}
    >
      <TimeInput
        value={slot.startTime}
        onChange={val => onChange(dayIndex, slotIndex, 'startTime', val)}
        on24HourDetected={(period: 'AM' | 'PM', convertedTime: string) => {
          onChangeWithPeriod(
            dayIndex,
            slotIndex,
            'startTime',
            convertedTime,
            period,
          )
        }}
        placeholder="9:00"
        isValid={isStartValid}
        hasRangeError={hasRangeError}
      />
      <PeriodSelect
        value={slot.startPeriod}
        onChange={val => onChange(dayIndex, slotIndex, 'startPeriod', val)}
        hasRangeError={hasRangeError}
      />
      <span style={{ margin: '0 8px', color: '#BDBDBD' }}>-</span>
      <TimeInput
        value={slot.endTime}
        onChange={val => onChange(dayIndex, slotIndex, 'endTime', val)}
        on24HourDetected={(period: 'AM' | 'PM', convertedTime: string) => {
          onChangeWithPeriod(
            dayIndex,
            slotIndex,
            'endTime',
            convertedTime,
            period,
          )
        }}
        placeholder="5:00"
        isValid={isEndValid}
        hasRangeError={hasRangeError}
      />
      <PeriodSelect
        value={slot.endPeriod}
        onChange={val => onChange(dayIndex, slotIndex, 'endPeriod', val)}
        hasRangeError={hasRangeError}
      />
    </div>
  )
})

TimeSlotInput.displayName = 'TimeSlotInput'

// Main Component
const ScheduleField: React.FC<ScheduleFieldProps> = ({
  value,
  onChange,
  errors,
  touched,
  onValidationChange,
}) => {
  // Validate schedule whenever it changes
  useEffect(() => {
    const validationResult = ScheduleValidator.validateSchedule(value)
    onValidationChange?.(validationResult.isValid)
  }, [value, onValidationChange])

  const handleScheduleChange = useCallback(
    (newDays: DaySchedule[]) => {
      const newSchedule = { days: newDays }
      onChange(newSchedule)
    },
    [onChange],
  )

  // Handlers that update the schedule
  const handleAddTimeSlot = useCallback(
    (dayIdx: number) => {
      const newDays = value.days.map((day, i) =>
        i === dayIdx
          ? {
              ...day,
              timeSlots: [...day.timeSlots, createDefaultTimeSlot()],
            }
          : day,
      )
      handleScheduleChange(newDays)
    },
    [value.days, handleScheduleChange],
  )

  const handleRemoveTimeSlot = useCallback(
    (dayIdx: number, slotIdx: number) => {
      const newDays = value.days.map((day, i) =>
        i === dayIdx
          ? {
              ...day,
              timeSlots: day.timeSlots.filter((_, j) => j !== slotIdx),
            }
          : day,
      )
      handleScheduleChange(newDays)
    },
    [value.days, handleScheduleChange],
  )

  const handleSetAllDay = useCallback(
    (dayIdx: number, allDay: boolean) => {
      const newDays = value.days.map((day, i) =>
        i === dayIdx
          ? {
              ...day,
              timeSlots: allDay
                ? [createAllDaySlot()]
                : [createDefaultTimeSlot()],
            }
          : day,
      )
      handleScheduleChange(newDays)
    },
    [value.days, handleScheduleChange],
  )

  const handleToggleDay = useCallback(
    (dayIdx: number) => {
      const newDays = value.days.map((day, i) =>
        i === dayIdx
          ? day.enabled
            ? {
                ...day,
                enabled: false,
                timeSlots: [],
              }
            : {
                ...day,
                enabled: true,
                timeSlots: [createDefaultTimeSlot()],
              }
          : day,
      )
      handleScheduleChange(newDays)
    },
    [value.days, handleScheduleChange],
  )

  const handleUpdateTimeField = useCallback(
    (
      dayIdx: number,
      slotIdx: number,
      field: keyof TimeSlot,
      fieldValue: string,
    ) => {
      const newDays = value.days.map((day, i) =>
        i === dayIdx
          ? {
              ...day,
              timeSlots: day.timeSlots.map((slot, j) =>
                j === slotIdx ? { ...slot, [field]: fieldValue } : slot,
              ),
            }
          : day,
      )
      handleScheduleChange(newDays)
    },
    [value.days, handleScheduleChange],
  )

  const handleUpdateTimeWithPeriod = useCallback(
    (
      dayIdx: number,
      slotIdx: number,
      timeField: 'startTime' | 'endTime',
      newValue: string,
      period: 'AM' | 'PM',
    ) => {
      const newDays = value.days.map((day, i) =>
        i === dayIdx
          ? {
              ...day,
              timeSlots: day.timeSlots.map((slot, j) =>
                j === slotIdx
                  ? {
                      ...slot,
                      [timeField]: newValue,
                      [timeField === 'startTime' ? 'startPeriod' : 'endPeriod']:
                        period,
                    }
                  : slot,
              ),
            }
          : day,
      )
      handleScheduleChange(newDays)
    },
    [value.days, handleScheduleChange],
  )

  return (
    <FlexColumn style={{ gap: '8px' }}>
      {value.days.map((day, dayIdx) => (
        <React.Fragment key={day.id}>
          {/* Always render the first row for the day */}
          <FlexRow
            style={{ alignItems: 'center', gap: '8px', height: ROW_HEIGHT }}
            key={`${day.id}-row-0`}
          >
            {/* Day toggle and label */}
            <div style={{ width: 132, display: 'flex', alignItems: 'center' }}>
              <SuperSwitch
                checked={day.enabled}
                onChange={() => handleToggleDay(dayIdx)}
              />
              <span style={{ marginLeft: 8 }}>{day.label}</span>
            </div>
            {/* All day checkbox */}
            {day.enabled && (
              <div
                style={{
                  minWidth: 76,
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() =>
                  handleSetAllDay(
                    dayIdx,
                    !(day.timeSlots.length === 1 && day.timeSlots[0].allDay),
                  )
                }
              >
                {day.timeSlots.length === 1 && day.timeSlots[0].allDay ? (
                  <MdCheckBox style={{ fontSize: 18, marginRight: 6 }} />
                ) : (
                  <MdCheckBoxOutlineBlank
                    style={{ fontSize: 18, marginRight: 6 }}
                  />
                )}
                <span>All day</span>
              </div>
            )}
            {/* Time slot input for first slot */}
            {day.enabled && !day.timeSlots.every(s => s.allDay) && (
              <>
                <TimeSlotInput
                  slot={day.timeSlots[0]}
                  dayIndex={dayIdx}
                  slotIndex={0}
                  onChange={handleUpdateTimeField}
                  onChangeWithPeriod={handleUpdateTimeWithPeriod}
                />
                {/* Plus button only on first slot */}
                <button
                  type="button"
                  aria-label="Add time range"
                  style={{
                    marginTop: 4,
                    color: '#000',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 18,
                    alignSelf: 'flex-start',
                  }}
                  onClick={() => handleAddTimeSlot(dayIdx)}
                >
                  +
                </button>
              </>
            )}
          </FlexRow>
          {/* Render additional slots, stacked and indented */}
          {day.enabled &&
            !day.timeSlots.every(s => s.allDay) &&
            day.timeSlots.slice(1).map((slot, slotIdx) => (
              <FlexRow
                key={slot.id}
                style={{ alignItems: 'center', gap: '8px', height: ROW_HEIGHT }}
              >
                {/* Indent for alignment */}
                <div style={{ width: 128 }} />
                <div style={{ minWidth: 80 }} />
                <TimeSlotInput
                  slot={slot}
                  dayIndex={dayIdx}
                  slotIndex={slotIdx + 1}
                  onChange={handleUpdateTimeField}
                  onChangeWithPeriod={handleUpdateTimeWithPeriod}
                />
                {/* Remove button only on subsequent slots */}
                <button
                  type="button"
                  aria-label="Remove time range"
                  style={{
                    color: '#000',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 18,
                  }}
                  onClick={() => handleRemoveTimeSlot(dayIdx, slotIdx + 1)}
                >
                  -
                </button>
              </FlexRow>
            ))}
        </React.Fragment>
      ))}
      {/* Place improved validation messages at the bottom of all days */}
      {(() => {
        const validationResult = ScheduleValidator.validateSchedule(value)
        if (!validationResult.isValid) {
          return (
            <div className="text-sm text-red-600" style={{ marginTop: 12 }}>
              {(() => {
                // Map general error to a set of days
                const errorTypeToDays: Record<string, Set<string>> = {}
                const generalError =
                  'Invalid time range, invalid format, or time slots overlap'
                validationResult.errors.forEach(err => {
                  const match = err.match(/^([A-Za-z]+): (.+)$/)
                  if (match) {
                    const [, day] = match
                    if (!errorTypeToDays[generalError]) {
                      errorTypeToDays[generalError] = new Set()
                    }
                    errorTypeToDays[generalError].add(day)
                  }
                })
                return Object.entries(errorTypeToDays).map(
                  ([errorType, days]) => (
                    <div key={errorType} className="mb-1">
                      {errorType} for{' '}
                      {Array.from(days)
                        .join(', ')
                        .replace(/, ([^,]*)$/, ' and $1')}
                      .
                    </div>
                  ),
                )
              })()}
            </div>
          )
        }
        return null
      })()}
      {errors?.timeRestrictions && touched?.timeRestrictions && (
        <div className="text-sm text-red-600">{errors.timeRestrictions}</div>
      )}
    </FlexColumn>
  )
}

export default React.memo(ScheduleField)
